import {
  BLACK,
  EMPTY,
  WHITE,
  chains,
  groupsInAtari,
  type Color,
  type Game,
  type Position,
  type Vertex,
} from '@baduk/engine';
import type { AtariMark, ChainEdge, ChainOutline, EyeMark, LibertyCount } from './components/Board.js';

/**
 * Training wheels.
 *
 * Everything here answers a question a beginner cannot yet answer at a glance,
 * and every one of them is switchable, because the point is to stop needing
 * them. They are read straight off the rules — the same functions the bots and
 * the coach use — so a helper can never tell a learner something the engine
 * would contradict.
 */
export interface Helpers {
  /** Letters and numbers around the edge. */
  readonly coordinates: boolean;
  /** Hovering a stone lights up its group and its liberties. */
  readonly liberties: boolean;
  /** Ring every group that is one move from being captured. */
  readonly atari: boolean;
  /** Mark the eyes that are actually eyes. */
  readonly eyes: boolean;
  /** Cross out the points the ko rule forbids this turn. */
  readonly ko: boolean;
  /** Grade the point under the cursor before it is played. */
  readonly feedback: boolean;
  /** Write each group's liberty count on the board. */
  readonly counts: boolean;
  /** Trace the perimeter of every connected group. */
  readonly outlines: boolean;
}

export const DEFAULT_HELPERS: Helpers = {
  coordinates: true,
  liberties: true,
  atari: false,
  eyes: false,
  ko: true,
  feedback: false,
  counts: false,
  outlines: true,
};

/** What each toggle is called and what it is for, in the order they are shown. */
export const HELPER_LABELS: ReadonlyArray<{
  readonly key: keyof Helpers;
  readonly label: string;
  readonly hint: string;
}> = [
  {
    key: 'liberties',
    label: 'Liberties on hover',
    hint: 'Hover any stone to light up its group and the empty points keeping it alive.',
  },
  {
    key: 'atari',
    label: 'Atari warnings',
    hint: 'Rings any group down to its last liberty, either colour, and marks the point that takes it.',
  },
  {
    key: 'eyes',
    label: 'Eyes',
    hint: 'Marks real eyes only. Two of them and a group can never be captured.',
  },
  {
    key: 'outlines',
    label: 'Group outlines',
    hint: 'Traces the edge of every connected group, so which stones are one unit is visible without counting.',
  },
  {
    key: 'counts',
    label: 'Liberty counts',
    hint: 'Writes the number of liberties on every group, both colours. Counting them yourself is the skill; this is the answer key.',
  },
  {
    key: 'ko',
    label: 'Ko bans',
    hint: 'Crosses out a point the ko rule forbids this turn — the one place a sensible move is illegal.',
  },
  {
    key: 'feedback',
    label: 'Live move feedback',
    hint: 'Grades the point under your cursor before you play it — Excellent to Bad, with the reason. The strongest crutch here: try guessing first, then check.',
  },
  {
    key: 'coordinates',
    label: 'Coordinates',
    hint: 'The letters and numbers the coach uses when it names a point.',
  },
];

/**
 * Every group with one liberty left, both colours, with the point that decides
 * it. Shown for both sides on purpose: half of learning atari is noticing when
 * it is the opponent who is one move from losing something.
 */
export function atariMarks(position: Position): AtariMark[] {
  const marks: AtariMark[] = [];
  for (const color of [BLACK, WHITE] as const) {
    for (const group of groupsInAtari(position, color)) {
      marks.push({ color, stones: group.stones, point: group.liberties[0] });
    }
  }
  return marks;
}

/**
 * Points that are real eyes — the strict test, which also wants the diagonals.
 * A false eye marked as an eye would teach exactly the wrong lesson, since
 * believing in one is how beginners lose groups they were sure of.
 */
export function eyeMarks(position: Position): EyeMark[] {
  const marks: EyeMark[] = [];
  for (let v = 0; v < position.area; v++) {
    if (position.at(v) !== EMPTY) continue;
    for (const color of [BLACK, WHITE] as const) {
      if (position.isTrueEye(v, color)) {
        marks.push({ vertex: v, color });
        break;
      }
    }
  }
  return marks;
}

/**
 * The perimeter of every connected group.
 *
 * Which stones form one group is the thing a beginner most often gets wrong, and
 * it is invisible: two touching stones are one unit while two diagonal ones are
 * not, and nothing on the board says so. An outline says it without a word.
 */
export function chainOutlines(position: Position): ChainOutline[] {
  const size = position.size;
  const outlines: ChainOutline[] = [];

  for (const color of [BLACK, WHITE] as const) {
    for (const chain of chains(position, color)) {
      const members = new Set(chain.stones);
      const edges: ChainEdge[] = [];

      // A side is on the perimeter when the neighbour across it is not family —
      // which includes the edge of the board itself.
      for (const stone of chain.stones) {
        const x = stone % size;
        const y = Math.floor(stone / size);
        if (y === 0 || !members.has(stone - size)) edges.push({ vertex: stone, side: 'top' });
        if (y === size - 1 || !members.has(stone + size)) {
          edges.push({ vertex: stone, side: 'bottom' });
        }
        if (x === 0 || !members.has(stone - 1)) edges.push({ vertex: stone, side: 'left' });
        if (x === size - 1 || !members.has(stone + 1)) edges.push({ vertex: stone, side: 'right' });
      }
      outlines.push({ color, edges });
    }
  }
  return outlines;
}

/**
 * How many liberties each group has, one number per group rather than per stone.
 *
 * Counting liberties is the first real skill in Go and the one every later idea
 * rests on, so this is squarely an answer key: useful to check yourself against,
 * and worth switching off as soon as the count comes without effort.
 */
export function libertyCounts(position: Position, avoid: Vertex | null = null): LibertyCount[] {
  const marks: LibertyCount[] = [];
  for (const color of [BLACK, WHITE] as const) {
    for (const chain of chains(position, color)) {
      marks.push({
        vertex: labelStone(chain.stones, position.size, avoid),
        color,
        count: chain.liberties.length,
      });
    }
  }
  return marks;
}

/**
 * Which stone carries a group's number: the one nearest the middle of the group,
 * so the label reads as belonging to the whole chain rather than to one stone on
 * its edge. The last move is skipped where there is any alternative, since it is
 * already wearing a marker of its own.
 */
function labelStone(stones: readonly Vertex[], size: number, avoid: Vertex | null): Vertex {
  const pool = stones.length > 1 ? stones.filter((s) => s !== avoid) : stones;
  const usable = pool.length > 0 ? pool : stones;

  let sumX = 0;
  let sumY = 0;
  for (const stone of usable) {
    sumX += stone % size;
    sumY += Math.floor(stone / size);
  }
  const midX = sumX / usable.length;
  const midY = sumY / usable.length;

  let best = usable[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const stone of usable) {
    const dx = (stone % size) - midX;
    const dy = Math.floor(stone / size) - midY;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = stone;
    }
  }
  return best;
}

/**
 * Points `color` may not play right now because of ko or superko.
 *
 * Suicide is deliberately not included. It is forbidden everywhere on the board
 * at all times and marking it would litter the board, whereas a ko ban is a
 * single point that appears for one turn and baffles everyone the first time.
 */
export function forbiddenPoints(game: Game, color: Color): Vertex[] {
  const points: Vertex[] = [];
  for (let v = 0; v < game.position.area; v++) {
    if (game.position.at(v) !== EMPTY) continue;
    const reason = game.illegalReason({ type: 'play', vertex: v }, color);
    if (reason === 'ko' || reason === 'superko') points.push(v);
  }
  return points;
}
